/**
 * ThemeSettings Component
 * Allows users to customize their color theme, dark/light mode, and accessibility settings
 * Now integrated with ThemeContext for real-time preview and persistence
 */

import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Settings, Palette, Type, Eye, Sparkles, Loader2, Check,
  Sun, Moon, Monitor, Zap
} from "lucide-react";
import { cn } from "@/lib/utils";

const FONT_SIZES = [
  { value: "small", label: "S", fullLabel: "Small" },
  { value: "medium", label: "M", fullLabel: "Medium" },
  { value: "large", label: "L", fullLabel: "Large" },
  { value: "xlarge", label: "XL", fullLabel: "Extra Large" }
];

export default function ThemeSettings({ trigger }) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    theme,
    setTheme,
    useSystemTheme,
    setUseSystemTheme,
    colorScheme,
    setColorScheme,
    colorSchemes,
    highContrast,
    setHighContrast,
    accessibility,
    updateAccessibility,
    isDark,
    isSyncing
  } = useTheme();

  const handleModeChange = (mode) => {
    if (mode === 'system') {
      setUseSystemTheme(true);
    } else {
      setUseSystemTheme(false);
      setTheme(mode);
    }
    toast.success(`Theme set to ${mode === 'system' ? 'system preference' : mode} mode`);
  };

  const handleColorChange = (scheme) => {
    setColorScheme(scheme);
    toast.success(`Color theme changed to ${colorSchemes[scheme]?.name}`);
  };

  const getCurrentMode = () => {
    if (useSystemTheme) return 'system';
    return theme;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Settings className="w-4 h-4" />
            Theme Settings
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" style={{ color: colorSchemes[colorScheme]?.primary }} />
            Theme & Appearance Settings
            {isSyncing && (
              <Badge variant="secondary" className="gap-1 text-xs ml-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[70vh] pr-4">
          <div className="space-y-6 py-4">
            {/* Appearance Mode */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  Appearance Mode
                </CardTitle>
                <CardDescription>Choose your preferred display mode</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleModeChange('light')}
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all text-center",
                      getCurrentMode() === 'light'
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                    data-testid="mode-light"
                  >
                    <Sun className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    <p className="font-medium text-sm">Light</p>
                    <p className="text-xs text-muted-foreground">Bright & clear</p>
                  </button>
                  <button
                    onClick={() => handleModeChange('dark')}
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all text-center",
                      getCurrentMode() === 'dark'
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                    data-testid="mode-dark"
                  >
                    <Moon className="w-6 h-6 mx-auto mb-2 text-indigo-500" />
                    <p className="font-medium text-sm">Dark</p>
                    <p className="text-xs text-muted-foreground">Easy on the eyes</p>
                  </button>
                  <button
                    onClick={() => handleModeChange('system')}
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all text-center",
                      getCurrentMode() === 'system'
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                    data-testid="mode-system"
                  >
                    <Monitor className="w-6 h-6 mx-auto mb-2 text-slate-500" />
                    <p className="font-medium text-sm">System</p>
                    <p className="text-xs text-muted-foreground">Follow device</p>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Color Theme Selection */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Color Theme
                    </CardTitle>
                    <CardDescription>Select your preferred accent color</CardDescription>
                  </div>
                  <Badge className="gap-1" style={{ 
                    backgroundColor: colorSchemes[colorScheme]?.primary + '20',
                    color: colorSchemes[colorScheme]?.primary 
                  }}>
                    <Sparkles className="w-3 h-3" />
                    12 Themes
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {Object.entries(colorSchemes).map(([key, scheme]) => (
                    <button
                      key={key}
                      onClick={() => handleColorChange(key)}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-left group",
                        colorScheme === key
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-border hover:border-primary/50"
                      )}
                      data-testid={`color-theme-${key}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-5 h-5 rounded-full ring-2 ring-white/20"
                          style={{ backgroundColor: scheme.primary }}
                        />
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: scheme.secondary }}
                        />
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: scheme.accent }}
                        />
                        {colorScheme === key && (
                          <Check className="w-4 h-4 ml-auto" style={{ color: scheme.primary }} />
                        )}
                      </div>
                      <p className="text-xs font-medium truncate">{scheme.name}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Accessibility */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Accessibility
                </CardTitle>
                <CardDescription>Customize for better visibility and comfort</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* High Contrast */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="high-contrast" className="font-medium">
                      High Contrast Mode
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Increases contrast for better visibility
                    </p>
                  </div>
                  <Switch
                    id="high-contrast"
                    checked={highContrast}
                    onCheckedChange={(checked) => {
                      setHighContrast(checked);
                      toast.success(checked ? 'High contrast enabled' : 'High contrast disabled');
                    }}
                    data-testid="high-contrast-toggle"
                  />
                </div>

                <Separator />

                {/* Reduce Motion */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="reduce-motion" className="font-medium flex items-center gap-2">
                      <Zap className="w-3 h-3" />
                      Reduce Motion
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Minimizes animations and transitions
                    </p>
                  </div>
                  <Switch
                    id="reduce-motion"
                    checked={accessibility.reduceMotion}
                    onCheckedChange={(checked) => {
                      updateAccessibility({ reduceMotion: checked });
                      toast.success(checked ? 'Motion reduced' : 'Motion enabled');
                    }}
                    data-testid="reduce-motion-toggle"
                  />
                </div>

                <Separator />

                {/* Font Size */}
                <div className="space-y-3">
                  <div className="space-y-0.5">
                    <Label className="font-medium flex items-center gap-2">
                      <Type className="w-3 h-3" />
                      Font Size
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Adjust text size for readability
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {FONT_SIZES.map((size) => (
                      <Button
                        key={size.value}
                        variant={accessibility.fontSize === size.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          updateAccessibility({ fontSize: size.value });
                          toast.success(`Font size set to ${size.fullLabel}`);
                        }}
                        className="flex-1"
                        data-testid={`font-size-${size.value}`}
                      >
                        {size.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Live Preview */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Live Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="p-4 rounded-lg border transition-colors"
                  style={{ 
                    backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    borderColor: isDark ? '#334155' : '#e2e8f0'
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: colorSchemes[colorScheme]?.primary }}
                    >
                      GC
                    </div>
                    <div>
                      <p className="font-semibold">Goodwin Cares</p>
                      <p className="text-sm opacity-70">Hospice Operating System</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                      style={{ backgroundColor: colorSchemes[colorScheme]?.primary }}
                    >
                      Primary Action
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{ 
                        backgroundColor: colorSchemes[colorScheme]?.primary + '15',
                        color: colorSchemes[colorScheme]?.primary
                      }}
                    >
                      Secondary
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
                      style={{ 
                        borderColor: isDark ? '#475569' : '#cbd5e1',
                        color: isDark ? '#94a3b8' : '#64748b'
                      }}
                    >
                      Outline
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge style={{ 
                      backgroundColor: colorSchemes[colorScheme]?.primary,
                      color: 'white'
                    }}>
                      Active
                    </Badge>
                    <Badge variant="outline" style={{ 
                      borderColor: colorSchemes[colorScheme]?.secondary,
                      color: colorSchemes[colorScheme]?.secondary
                    }}>
                      {colorSchemes[colorScheme]?.name}
                    </Badge>
                    <Badge variant="secondary">
                      {isDark ? 'Dark Mode' : 'Light Mode'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
